import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGridApiContext, gridFilterModelSelector } from '../lib/dataGrid';
import {
    gridSessionKey,
    isFiltering,
    loadGridSession,
    saveGridSession
} from './gridSessionState';

/**
 * Scrolling fires per frame and each save serialises the whole store, so the
 * offset is written at most this often — plus once more when the grid goes away.
 */
const SCROLL_SAVE_INTERVAL = 500;

/** How many frames a restore may wait for the grid to know how tall it is. */
const SCROLL_RESTORE_TRIES = 10;

/**
 * Put this grid back where it was left this session: its filter and its scroll
 * offset (see gridSessionState for what that means and why it is a session).
 *
 * The sibling of `useGridPreferences`, mounted from the same place and running
 * inside the grid for the same reason — GridCrudToolbar has the api from
 * context, so no grid needs to plumb an apiRef through.
 *
 * Two things a stored state must never do:
 *
 * - Override a LINK. A grid opened through `?date=` arrives already filtered to
 *   that day (gridDeepLink), and that is an instruction, not a leftover.
 * - Save the empty offset it is about to correct. An empty grid reports 0 and
 *   the rows arrive an RxDB tick after the mount, so scrolling is only recorded
 *   once the restore it is waiting for has happened.
 */
export function useGridSessionState(gridId: string | undefined): void {
    const apiRef = useGridApiContext();
    // The record grids are per project and share one id per entity; the
    // cross-project ones (Auswertung, Zeiterfassung) have no project param and
    // fall back to the id alone.
    const { projectId } = useParams();

    useEffect(() => {
        if (!gridId) {
            return;
        }
        const key = gridSessionKey(gridId, projectId);
        const saved = loadGridSession(key);

        if (
            saved.filterModel &&
            !isFiltering(gridFilterModelSelector(apiRef))
        ) {
            apiRef.current.setFilterModel(saved.filterModel);
        }

        // The offset still owed to the user — held back until there are rows to
        // scroll through, and until then the reason nothing is written.
        let pending = saved.scrollTop ?? 0;
        let frame = 0;
        /**
         * Scroll there, and keep asking until it sticks.
         *
         * A grid cannot scroll past a height it has not measured, and coming
         * back from a dialog it has its rows but no layout yet — the first
         * frame's `scroll` is silently clamped to the top. Re-reading the
         * position is the only way to tell that from a list that is genuinely
         * shorter now, and that one must not spin: the tries are counted out.
         */
        const scrollTo = (top: number, tries: number) => {
            frame = requestAnimationFrame(() => {
                const api = apiRef.current;
                if (!api) {
                    return;
                }
                api.scroll({ top });
                if (tries > 0 && api.getScrollPosition().top < top - 1) {
                    scrollTo(top, tries - 1);
                    return;
                }
                pending = 0;
            });
        };
        const restoreScroll = (): boolean => {
            if (pending <= 0) {
                return true;
            }
            if (apiRef.current.getRowsCount() === 0) {
                return false;
            }
            scrollTo(pending, SCROLL_RESTORE_TRIES);
            return true;
        };
        let restored = restoreScroll();

        let lastTop = saved.scrollTop ?? 0;
        let lastSaved = 0;
        const unsubscribes = [
            apiRef.current.subscribeEvent('rowsSet', () => {
                if (!restored) {
                    restored = restoreScroll();
                }
            }),
            apiRef.current.subscribeEvent('scrollPositionChange', (params) => {
                if (pending > 0) {
                    return;
                }
                lastTop = params.top;
                const now = Date.now();
                if (now - lastSaved >= SCROLL_SAVE_INTERVAL) {
                    lastSaved = now;
                    saveGridSession(key, { scrollTop: params.top });
                }
            }),
            apiRef.current.subscribeEvent('filterModelChange', (model) => {
                saveGridSession(key, { filterModel: model });
            })
        ];
        return () => {
            cancelAnimationFrame(frame);
            for (const unsubscribe of unsubscribes) {
                unsubscribe();
            }
            // The throttled writes above lag behind the last frame scrolled.
            saveGridSession(key, { scrollTop: lastTop });
        };
    }, [apiRef, gridId, projectId]);
}
